import { Component, ViewChild, Input, ElementRef, Output, EventEmitter, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DigitFormatterPipe } from '../../../shared/pipe/digit-formatter.pipe';
import * as d3 from 'd3';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ReportsService } from '../../reports.service';
import { InbuiltUtilityService } from '../inbuilt-utility.service';

@Component({
    selector: 'app-inbuilt-donut-chart',
    templateUrl: './inbuilt-donut-chart.component.html',
    styleUrls: ['./inbuilt-donut-chart.component.css'],
    standalone: true,
    imports: [CommonModule],
    providers: [DigitFormatterPipe]
})
export class InbuiltDonutChartComponent {
  @ViewChild('chart', { static: true, read: ElementRef }) private chartContainer: ElementRef;
  @Input() public title;
  @Input() public titleMiddle = 'TCO';
  @Input() public type = 'PieChart';
  @Input() public chartData;
  @Input() public columnNames;
  @Input() public options;
  @Input() public total;
  @Input() public selectedYear;
  @Input() public isInfraChart:boolean=false;
  @Output() chartReady = new EventEmitter<boolean>();
  @Output() seriesChange = new EventEmitter<Array<{ label: string; value: number; color: string; formattedValue: string; metricType: string }>>();
  @Input() config: any;
  @Input() elementData: any;
  @Input() height = 200;
  @Input() width = 300;
  @Input() showCurrancy :boolean = true;
  @Input() showMiddleTitle: boolean = true;
  @Input() showChartTitle : boolean = false;
  @Input() pieLabelFont = 10;
  @Input() externalLabelScale: number = 1;
  @Input() customStyle='chart-title';
  totalCount;
  public amountTotal: any = 0;
  public chartLoaded: boolean = false;
  public loading = false;
  public errorMessage = '';
  public noDataFound = false;
  tooltip: any;
  label1;
  arcGenerator;
  private readonly destroy$ = new Subject<void>();
  private selectedFilters: any = {};
  private lastRequestSignature = '';
  private readonly defaultPalette = ['#78C89F', '#EFB46B', '#388DDC', '#013b71', '#F43F5E'];

  constructor(
    private digitFormatter: DigitFormatterPipe,
    private container: ElementRef,
    private reportsService: ReportsService,
    private inbuiltUtilityService: InbuiltUtilityService
  ) {

  }

  ngOnInit(): void {
    if (this.hasApiContext()) {
      this.selectedFilters = this.config?.selectedFilters || {};
      this.applyDisplayConfigFromElement();
      this.subscribeToFilterEvents();
      this.fetchDonutData(true);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.hasApiContext()) {
      this.applyDisplayConfigFromElement();

      if ((changes['config'] && !changes['config'].firstChange) || (changes['elementData'] && !changes['elementData'].firstChange)) {
        this.selectedFilters = this.config?.selectedFilters || this.selectedFilters;
        this.fetchDonutData(false);
      }
      return;
    }

    this.updateAmountTotalFromInput();
    this.renderChart();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private hasApiContext(): boolean {
    return !!this.config && !!this.elementData;
  }

  private applyDisplayConfigFromElement(): void {
    const explicitShowCurrency = this.elementData?.config?.showCurrency;
    if (explicitShowCurrency !== undefined && explicitShowCurrency !== null) {
      this.showCurrancy = this.resolveBoolean(explicitShowCurrency, this.showCurrancy);
    }
  }

  private updateAmountTotalFromInput(): void {
    if (!this.total) {
      this.amountTotal = this.digitFormatter.transform(0, 1);
      return;
    }

    const value = parseFloat(String(this.total).replace(/,/g, ''));
    this.amountTotal = this.digitFormatter.transform(Number.isFinite(value) ? value : 0, 1);
  }

  private renderChart(): void {
    if (this.chartData && this.isInfraChart) {
      this.drawInfraPieChart();
      return;
    }

    if (this.chartData) {
      this.drawPieChart();
    }
  }

  private subscribeToFilterEvents(): void {
    this.inbuiltUtilityService.getSelectedFilterEvents()
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: any) => {
        if (!this.shouldApplyFilterEvent(event)) {
          return;
        }

        this.selectedFilters = event?.selectedFilters || {};
        this.fetchDonutData(false);
      });
  }

  private shouldApplyFilterEvent(event: any): boolean {
    if (!event || !this.config) {
      return false;
    }

    const eventReport = String(event?.reportNameFromQueryParams ?? event?.report ?? '');
    const eventPage = String(event?.currentPage?.key ?? event?.page ?? '');
    const eventTab = String(event?.currentTab?.key ?? event?.tab ?? '');
    const myReport = String(this.config?.reportNameFromQueryParams ?? '');
    const myPage = String(this.config?.currentPage?.key ?? '');
    const myTab = String(this.config?.currentTab?.key ?? '');

    if (eventReport && eventReport !== myReport) {
      return false;
    }
    if (eventPage && eventPage !== myPage) {
      return false;
    }
    if (eventTab && eventTab !== myTab) {
      return false;
    }

    return true;
  }

  private fetchDonutData(force: boolean): void {
    const selectedFiltersPayload = this.inbuiltUtilityService.getTablePayload(this.selectedFilters || {});
    const nextSignature = this.buildRequestSignature(selectedFiltersPayload);
    if (!force && nextSignature === this.lastRequestSignature) {
      return;
    }

    this.lastRequestSignature = nextSignature;
    this.loading = true;
    this.errorMessage = '';

    const payload = {
      report: this.config?.reportNameFromQueryParams,
      page: this.config?.currentPage ? this.config.currentPage.key : '',
      tab: this.config?.currentTab ? this.config.currentTab.key : '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || 'donut_chart',
      download: false,
      selectedFilters: selectedFiltersPayload,
    };

    this.reportsService.getInbuildTableData(payload)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          this.bindResponseToChart(response);
        },
        error: (error: any) => {
          console.error('Error fetching donut data: ', error);
          this.noDataFound = true;
          this.chartData = [];
          this.total = '0';
          this.updateAmountTotalFromInput();
          this.seriesChange.emit([]);
          this.renderChart();
        }
      });
  }

  private bindResponseToChart(response: any): void {
    const rows = Array.isArray(response?.data) ? response.data : [];
    this.noDataFound = rows.length === 0;
    const groupByColumns = Array.isArray(response?.groupByColumns) ? response.groupByColumns : [];
    const columnInfo = response?.columnInfo || {};
    const slices = this.resolveSlicesFromResponse(rows, groupByColumns, columnInfo);

    this.chartData = slices.map((slice) => ({
      label: slice.label,
      value: slice.value,
      color: slice.color,
      tooltip: `${slice.label}: ${slice.formattedValue}`
    }));

    const totalValue = slices.reduce((sum, slice) => sum + slice.value, 0);
    this.total = totalValue.toLocaleString('en-US', { maximumFractionDigits: 2 });
    this.updateAmountTotalFromInput();
    this.seriesChange.emit(slices);
    this.renderChart();
  }

  private resolveSlicesFromResponse(
    rows: any[],
    groupByColumns: any[],
    columnInfo: Record<string, any>
  ): Array<{ label: string; value: number; color: string; formattedValue: string; metricType: string }> {
    const metricEntries = Object.keys(columnInfo || {});
    if (!Array.isArray(rows) || rows.length === 0) {
      return [];
    }

    if (groupByColumns.length > 0) {
      const labelKey = this.resolveMatchingKey(rows[0], groupByColumns[0]) || String(groupByColumns[0] || 'Category');
      const valueKey = this.pickGroupedValueKey(metricEntries, labelKey, rows[0]);
      const metricType = this.normalizeMetricType(columnInfo[valueKey]);
      const colors = this.resolveConfiguredColors();

      return rows.map((row: any, index: number) => {
        const label = String(row?.[labelKey] ?? `Item ${index + 1}`);
        const value = this.toNumber(row?.[valueKey]);
        return this.buildSlice(label, value, index, metricType, colors);
      });
    }

    const row = rows[0] || {};
    const keys = Object.keys(row);
    const colors = this.resolveConfiguredColors();

    return keys.map((key: string, index: number) => {
      const metricType = this.normalizeMetricType(columnInfo[key]);
      const value = this.toNumber(row[key]);
      return this.buildSlice(key, value, index, metricType, colors);
    });
  }

  private buildSlice(
    label: string,
    value: number,
    index: number,
    metricType: 'SUM' | 'COUNT'
  , colors: string[]): { label: string; value: number; color: string; formattedValue: string; metricType: string } {
    return {
      label,
      value,
      color: this.resolveSeriesColor(index, colors),
      formattedValue: metricType === 'SUM' ? `$${this.formatCompact(value, 1, 'cost')}` : this.formatCount(value),
      metricType
    };
  }

  private pickGroupedValueKey(metricEntries: string[], labelKey: string, sampleRow: any): string {
    const normalizedLabelKey = this.normalizeKey(labelKey);
    const metricMatch = metricEntries.find((key: string) => this.normalizeKey(key) !== normalizedLabelKey);
    if (metricMatch) {
      return metricMatch;
    }

    const sampleKeys = Object.keys(sampleRow || {});
    const rowMatch = sampleKeys.find((key: string) => this.normalizeKey(key) !== normalizedLabelKey);
    return rowMatch || labelKey;
  }

  private normalizeMetricType(value: any): 'SUM' | 'COUNT' {
    return String(value ?? '').trim().toUpperCase() === 'SUM' ? 'SUM' : 'COUNT';
  }

  private resolveMatchingKey(row: any, candidate: any): string | undefined {
    const keys = Object.keys(row || {});
    const normalizedCandidate = this.normalizeKey(candidate);
    return keys.find((key: string) => this.normalizeKey(key) === normalizedCandidate);
  }

  private resolveSeriesColor(index: number, colors: string[]): string {
    if (colors.length > 0) {
      return colors[index % colors.length];
    }

    return this.defaultPalette[index % this.defaultPalette.length];
  }

  private resolveConfiguredColors(): string[] {
    const rawColors = this.elementData?.config?.colors
      ?? this.elementData?.config?.palette
      ?? this.elementData?.config?.donutColors
      ?? this.config?.colors
      ?? this.config?.palette
      ?? [];

    if (Array.isArray(rawColors)) {
      return rawColors.map((color: any) => String(color || '').trim()).filter(Boolean);
    }

    if (typeof rawColors === 'string') {
      return rawColors.split(',').map((color: string) => color.trim()).filter(Boolean);
    }

    return [];
  }

  private formatCount(value: number): string {
    if (!Number.isFinite(value)) {
      return '0';
    }

    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  private formatCompact(value: number, decimals = 1, valueType: 'cost' | 'app' = 'cost'): string {
    const numeric = Number(value || 0);
    const abs = Math.abs(numeric);

    if (numeric === 0) {
      return '0';
    }

    if (abs < 1000) {
      return valueType === 'app' ? String(numeric) : numeric.toFixed(decimals);
    }

    const units = [
      { value: 1e9, symbol: 'B' },
      { value: 1e6, symbol: 'M' },
      { value: 1e3, symbol: 'K' }
    ];

    const unit = units.find((u) => abs >= u.value);
    if (!unit) {
      return String(numeric);
    }

    return `${(numeric / unit.value).toFixed(decimals)}${unit.symbol}`;
  }

  private buildRequestSignature(selectedFiltersPayload: any): string {
    return JSON.stringify({
      report: this.config?.reportNameFromQueryParams || '',
      page: this.config?.currentPage?.key || '',
      tab: this.config?.currentTab?.key || '',
      elementName: this.elementData?.name || '',
      elementType: this.elementData?.type || '',
      selectedFilters: selectedFiltersPayload || {}
    });
  }

  private resolveBoolean(value: any, fallback: boolean): boolean {
    if (value === undefined || value === null) {
      return fallback;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    return String(value).trim().toLowerCase() === 'true';
  }

  private normalizeKey(value: any): string {
    return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  private toNumber(value: any): number {
    if (value === null || value === undefined || value === '') {
      return 0;
    }

    const parsed = parseFloat(String(value).replace(/[$,%]/g, '').replace(/,/g, '').trim());
    return Number.isNaN(parsed) ? 0 : parsed;
  }
 
  onReady() {
    this.chartReady.next(true);
    this.chartLoaded = true;
    if (this.options.alignTitle && !this.options.titlePosition) {
      const abc = this.chartContainer.nativeElement;
      // TO ACCOMODATE MORE THAN 3 DONUT CHARTS IN A SINGLE CARD
      const node = abc.querySelector('text[x="81"]');
      if (node) {
        node.setAttribute('x', 10);
        node.setAttribute('y', 24);
        node.setAttribute('font-family', 'Arial Bold');
      }
    }
    if (this.options.legend && this.options.legend !== 'none' && this.options.legend !== 'right') {
      this.chartData.forEach(element => {
        const node = this.chartContainer.nativeElement.querySelector(`[column-id="${element[0]}"]`);
        if (node) {
          node.querySelector('text').setAttribute('x', 71);
          node.querySelector('circle').setAttribute('cx', 72);
        }
      });
    }
    this.totalCount = this.chartData.map(el => el[1]).reduce((curr, aggr) => curr + aggr, 0);
    this.modifyInnertext();
    const node = this.chartContainer.nativeElement.querySelectorAll('svg text');
    if (!this.chartData.length && node.length > 1) {
      const noData = node[1];
      noData.innerHTML = 'In Development';
      noData.setAttribute('font-size', '12');
    } else {
      const path = this.chartContainer.nativeElement.querySelectorAll('path');
      if (!path.length) {
        const noData = this.chartContainer.nativeElement.querySelector('svg');
        noData.innerHTML = `<text text-anchor="middle" x="150" y="102.8"
        font-family="Arial" font-size="12" stroke="none" stroke-width="0" fill="#000000">No data available</text>`;
      }
    }
  }

  private modifyInnertext() {
    const path = this.chartContainer.nativeElement.querySelectorAll('path');
    if (this.options.formatLabelText && typeof this.options.formatLabelText === 'function') {
      if (path.length > 0) {
        path.forEach(row => {
          // && row.nextSibling.innerHTML.indexOf('%') > 0
          if (row.nextSibling) {
            // let percentage = row.nextSibling.innerHTML;
            // percentage = +percentage.substr(0, percentage.length - 1);
            // const value = (percentage * this.totalCount) / 100;
            // const newVal = this.options.formatLabelText(value.toLocaleString());
            const value = parseFloat(row.nextSibling.innerHTML.replace(/,/g, ''));
            row.nextSibling.innerHTML = this.digitFormatter.transform(value, 1);
            row.nextSibling.setAttribute('x', +row.nextSibling.getAttribute('x') - 5);
            // row.nextSibling.innerHTML = newVal;
          }
        });
      }
    }

    if (path.length === 1) {
      path.forEach(row => {
        if (row.nextSibling) {
          row.nextSibling.setAttribute('x', 167);
          row.nextSibling.setAttribute('y', 145);
        }
      });
    }
  }

  appendValue() {
    const node = this.chartContainer.nativeElement.querySelectorAll('svg text');
    if (node.length === 1) {
      if (node[0].innerHTML.indexOf('%') == -1) {
        const value = parseFloat(node[0].innerHTML.replace(/,/g, ''));
        node[0].innerHTML = this.digitFormatter.transform(value, 1);
        node[0].setAttribute('x', 170);
        node[0].setAttribute('y', 145);
      }
    } else {
      node.forEach(item => {
        if (item.innerHTML.indexOf('%') == -1) {
          const value = parseFloat(item.innerHTML.replace(/,/g, ''));
          item.innerHTML = this.digitFormatter.transform(value, 1);
        }
      });
    }

  }

  drawPieChart() {
 // ----------------
    // Create a tooltip
    // ----------------
    // this.tooltip =  d3.select(this.container.nativeElement).select(".donut-chart")
    // .style("left", d3.event.pageX + "px")
    // .style("top", d3.event.pageY + "px")
    // .style("opacity", 1) 
    // .append("div")
    // .style("opacity", 0)
    // .attr("font", ".9vw sans-serif")
    // .attr("font-weight", "bold")
    // .attr("class", "stacktooltip")
    // The radius of the pieplot is half the width or half the height (smallest one). I subtract a bit of margin.
    const radius = 55;
    this.container.nativeElement.querySelectorAll('.pie_div svg').forEach(function (elem) {
      elem.parentNode.removeChild(elem);
    });
    // append the svg object to the div called 'my_dataviz'
    const svg = d3.select(this.container.nativeElement.querySelector('.pie_div'))
      .append("svg")
      .attr('preserveAspectRatio', 'xMidYMid meet')
      // .attr('height',function(d){ 
      //   if (this.titleMiddle === "TCO"){
      //     return (this.height) 
      //   } 
      //   return null;
      // })
      // .attr('width',function(d){ 
      //   if (this.titleMiddle === "TCO"){
      //     return (this.width) 
      //   } 
      //   return null;
      // })
      .attr('viewBox', `0 0 ${this.width} ${this.height}`)
      .append("g")
      .attr("transform", `translate(${this.width / 2},${this.height / 2})`)



    // Create dummy data
    const data = {}
    const sliceColors = [];
    const titles = [];
    this.chartData.filter(slice => {
      if (slice.value != 0) {
        data[slice.label] = slice.value;
        sliceColors.push(slice.color)
        titles.push(this.digitFormatter.transform(slice.value, 1));
      }

    })
    

    if (this.amountTotal == 0) {
      titles.push(this.digitFormatter.transform(0, 1));
      sliceColors.push("#F3F3F3");
      data['no_data'] = 1;
    }

    // set the color scale
    const color = d3.scaleOrdinal()
      .range(sliceColors)

    // Compute the position of each group on the pie:
    const pie = d3.pie()
      .value(d => d[1])

    const data_ready = pie(Object.entries(data))

    const arc = d3.arc()
      .innerRadius(100)         // This is the size of the donut hole
      .outerRadius(radius);

    this.arcGenerator = d3.arc()
      .innerRadius(0)
      .outerRadius(radius)
    // Build the pie chart: Basically, each part of the pie is a path that we build using the arc function.
    svg
      .selectAll('whatever')
      .data(data_ready)
      .join('path')
      .attr('d', arc)
      .attr('fill', d => color(d.data[0]))
      .on("mouseover",(event)=> {
  let filterdata=[];
  let html =  '';
  if (!event || !event.data || !event.data[0]) {
    d3.select(this.container.nativeElement).select(".donutcharttooltip").style("opacity", 0);
    return;
  }
  filterdata = this.chartData.filter(item=>{
      return (item['label']==event['data'][0]);
  });
  if(filterdata.length>0){
    html = filterdata[0].tooltip;
  }
 d3.select(this.container.nativeElement).select(".donutcharttooltip")
  .style("left", d3.event.offsetX+ "px")
  .style("top", d3.event.offsetY + "px")
 .style("opacity", 1)       
  .html(html)
})
      .on("mousemove", (event) => {
        let filterdata=[];
        let html =  '';       
        filterdata = this.chartData.filter(item=>{
            return (item['label']==event['data'][0]);
        });
        if(filterdata.length>0){
          html = filterdata[0].tooltip;
        }
       d3.select(this.container.nativeElement).select(".donutcharttooltip")
        .style("left", d3.event.offsetX+ "px")
        .style("top", d3.event.offsetY + "px")
       .style("opacity", 1)       
        .html(html)
      })
      .on("mouseout",()=> {
        setTimeout( () => {
          d3.select(this.container.nativeElement).select(".donutcharttooltip")
           .style("opacity", 0)
        },1000)
        // d3.select(this.container.nativeElement).select(".donutcharttooltip")
        // .style("opacity", 0)
      });
    // .attr("stroke", "black")
    // .style("stroke-width", "1px")

    svg
      .selectAll('whatever')
      .data(data_ready)
      .enter()
      .append('text')
      .text(function (d, i) { 
        return  (d.endAngle - d.startAngle) > 0.35? titles[i] : ''
      })
      .attr("transform", function (d) { 
        return "translate(" + arc.centroid(d) + ")"; 
      })
      .style("text-anchor", "middle")
      .style("font-size", this.pieLabelFont)
      .style('fill', '#FFFFFF').on("mouseover",()=> {
        // let html =  this.chartData[d].tooltip;        
        // d3.select(this.container.nativeElement).select(".donutcharttooltip")
        // .style("left", 50 + "px")
        // .style("top", 50 + "px")
        // .style("opacity", 1)       
        //  .html(html)
       })
       .on("mousemove", function() {
       
       })
       .on("mouseout", ()=> {
        //   d3.select(this.container.nativeElement).select(".donutcharttooltip")
        //  .style("opacity", 0)
       });

      if(this.showMiddleTitle) {
         this.label1 = svg.append("text")
        .attr("text-anchor", "middle")
        .text(this.titleMiddle + " " + this.selectedYear)
        .style("font-size", 13)
        .style("font-weight", 500);  
      }
    
    svg.append("text")
      .attr("text-anchor", "middle")
      .text( (this.showCurrancy ? '$' : '') + this.amountTotal)
      .style("font-size", this.showMiddleTitle ? 14: 16)
      .style("font-weight", 500)
      .attr('y', this.showMiddleTitle ? 15 : 5)
      // .selectAll("tspan")
      // .data(d => {
      //   console.log(d)
      //   // const lines = `${title(d.data)}`.split(/\n/);
      //   // return (d.endAngle - d.startAngle) > 0.25 ? lines : lines.slice(0, 1);
      // })

    this.chartReady.next(true);

  }

  drawInfraPieChart(){
    // ----------------
    // Create a tooltip
    // ----------------
    // this.tooltip =  d3.select(this.container.nativeElement).select(".donut-chart")
    // .style("left", d3.event.pageX + "px")
    // .style("top", d3.event.pageY + "px")
    // .style("opacity", 1) 
    // .append("div")
    // .style("opacity", 0)
    // .attr("font", ".9vw sans-serif")
    // .attr("font-weight", "bold")
    // .attr("class", "stacktooltip")
    // Compute responsive dimensions for infra chart
    const padding = 8;
    const effectiveWidth = Math.max(80, this.width || 300);
    const effectiveHeight = Math.max(60, this.height || 200);
    const radius = Math.floor(Math.max(8, Math.min(effectiveWidth, effectiveHeight) / 2 - padding));
    // choose inner radius proportional to outer radius so hole scales with size
    const innerRadius = Math.max(6, Math.floor(radius * 0.55));

    this.container.nativeElement.querySelectorAll('.pie_div svg').forEach(function (elem) {
      elem.parentNode.removeChild(elem);
    }); 
    // append the svg object to the div called 'my_dataviz'
    // add a small horizontal padding so external labels/tspans don't get clipped
    const calloutScale = Math.max(0.3, Math.min(1, Number(this.externalLabelScale) || 1));
    const labelFontPx = Math.max(18, Math.min(22, Math.floor(16 + calloutScale * 8)));
    const extraWidth = Math.max(160, Math.floor((Math.min(220, Math.max(90, Math.floor(effectiveWidth * 0.4)))) * calloutScale)); // adaptive horizontal padding for long labels
    const extraHeight = Math.max(90, Math.floor(labelFontPx * 3.2)); // vertical headroom for two-line labels
    const svgRoot = d3.select(this.container.nativeElement.querySelector('.pie_div'))
      .append("svg")
      .attr('preserveAspectRatio', 'xMidYMid meet')
      // expand viewBox to give room for outside labels and prevent clipping
      .attr('viewBox', `0 0 ${effectiveWidth + extraWidth} ${effectiveHeight + extraHeight}`)
      .attr('width', '100%')
      .attr('height', 'auto');

    // main container centered at middle (no rotation) — labels/polylines will be drawn here
    // shift the main container to the right by half of the extraWidth so the donut remains centered
    const container = svgRoot.append("g")
      .attr("transform", `translate(${(effectiveWidth + extraWidth) / 2},${(effectiveHeight + extraHeight) / 2})`);

    // group that contains only slices; we'll compute a rotation so first slice centers left
    const slicesG = container.append("g");



    // Create dummy data
    const data = {}
    const sliceColors = [];
    const titles = [];
    
    // Include all slices, even those with value 0
    this.chartData.forEach(slice => {
      // For display purposes, use a minimum value for 0 values to ensure they appear
      const displayValue = slice.value === 0 ? 0 : slice.value;
      data[slice.label] = displayValue;
      sliceColors.push(slice.color);
      titles.push(this.digitFormatter.transform(slice.value, 1));
    });

    // Only add no_data slice if chartData is empty
    if (this.chartData.length === 0 && this.amountTotal == 0) {
      titles.push(this.digitFormatter.transform(0, 1));
      sliceColors.push("#F3F3F3");
      data['no_data'] = 1;
    }

    // set the color scale
    const color = d3.scaleOrdinal()
      .range(sliceColors)

    // Compute the position of each group on the pie:
    const pie = d3.pie()
      .value(d => d[1])

    const data_ready = pie(Object.entries(data))

    // compute rotation so first slice midpoint aligns to the left (3*PI/2)
    let rotationDeg = 0;
    // expose rotationRad for use in label/polyline math
    let rotationRad = 0;
    if (data_ready && data_ready.length >= 2) {
      const firstMid = (data_ready[0].startAngle + data_ready[0].endAngle) / 2;
      const desiredMid = 3 * Math.PI / 2; // left side
      rotationRad = desiredMid - firstMid;
      rotationDeg = rotationRad * 180 / Math.PI;
      slicesG.attr('transform', `rotate(${rotationDeg})`);
    }

    // define arc and visible outer radius
    const arc = d3.arc()
      .innerRadius(innerRadius)
      .outerRadius(radius);

    this.arcGenerator = d3.arc()
      .innerRadius(0)
      .outerRadius(radius);

    // compute the visible outer radius (in case innerRadius > outer radius)
    const visibleOuterRadius = Math.max(innerRadius, radius);
    // outerArc generator for computing points on the true outer circumference
    const outerArc = d3.arc()
      .innerRadius(visibleOuterRadius)
      .outerRadius(visibleOuterRadius);
    // Build the pie chart: Basically, each part of the pie is a path that we build using the arc function.
    slicesG
      .selectAll('whatever')
      .data(data_ready)
      .join('path')
      .attr('d', arc)
      .attr('fill', d => color(d.data[0])).on("mouseover",(event)=> {
        let filterdata=[];
        let html =  '';       
        filterdata = this.chartData.filter(item=>{
            return (item['label']==event['data'][0]);
        });
        if(filterdata.length>0){
          html = filterdata[0].tooltip;
        }
       d3.select(this.container.nativeElement).select(".donutcharttooltip")
        .style("left", d3.event.offsetX+ "px")
        .style("top", d3.event.offsetY + "px")
       .style("opacity", 1)       
        .html(html)
      })
      .on("mousemove", (event) => {
        let filterdata=[];
        let html =  '';       
        filterdata = this.chartData.filter(item=>{
            return (item['label']==event['data'][0]);
        });
        if(filterdata.length>0){
          html = filterdata[0].tooltip;
        }
       d3.select(this.container.nativeElement).select(".donutcharttooltip")
        .style("left", d3.event.offsetX+ "px")
        .style("top", d3.event.offsetY + "px")
       .style("opacity", 1)       
        .html(html)
      })
      .on("mouseout",()=> {
        setTimeout( () => {
          d3.select(this.container.nativeElement).select(".donutcharttooltip")
           .style("opacity", 0)
        },1000)
        // d3.select(this.container.nativeElement).select(".donutcharttooltip")
        // .style("opacity", 0)
      });
    // .attr("stroke", "black")
    // .style("stroke-width", "1px")

    // Replace in-chart labels with external left/right labels and leader lines
    const self = this;
  // Offsets should scale with radius so labels stay readable on small screens
  const labelOffset = Math.max(14, Math.floor(radius * 1.2 * calloutScale)); // horizontal distance from donut to label
  const elbowOffset = Math.max(5, Math.floor(radius * 0.45 * calloutScale)); // horizontal stub before label
    const connectorLen = Math.max(6, Math.floor(12 * calloutScale)); // small vertical connector length (px)
  // ensure labels remain inside the svg bounds: compute effective offsets
  const halfWidth = (effectiveWidth + extraWidth) / 2;
  const maxAllowedOffset = Math.max(10, halfWidth - radius - 8); // leave 8px padding
  const labelOffsetEffective = Math.min(labelOffset, maxAllowedOffset);
  const elbowOffsetEffective = Math.min(elbowOffset, Math.max(6, labelOffsetEffective / 2));
  const minAngleToShow = 0.001; // show labels for even very small slices

    // Draw polylines (leader lines) from slice to external label point — draw in non-rotated container
    container
      .selectAll('polyline')
      .data(data_ready)
      .enter()
      .append('polyline')
      .attr('points', function(d) {
        const midAngle = (d.startAngle + d.endAngle) / 2;
        // get centroid on the outer circumference (in slice local coordinates)
        let outerCentroid = outerArc.centroid(d);
        let ox = outerCentroid && outerCentroid[0];
        let oy = outerCentroid && outerCentroid[1];
        if (!outerCentroid || isNaN(ox) || isNaN(oy)) {
          // fallback to polar math (use visibleOuterRadius to stay on true outer circumference)
          ox = Math.cos(midAngle - Math.PI / 2) * visibleOuterRadius;
          oy = Math.sin(midAngle - Math.PI / 2) * visibleOuterRadius;
        }
        // rotate that point by the slice rotation so it's in container coords
        const rotatedX = ox * Math.cos(rotationRad) - oy * Math.sin(rotationRad);
        const rotatedY = ox * Math.sin(rotationRad) + oy * Math.cos(rotationRad);
        // decide left/right based on the rotated centroid X sign so labels always go outward
        // (this avoids angle-range flips for very small/large slices)
        const side = (rotatedX < 0) ? -1 : 1;
        // label should sit outside the visible outer circumference
        const labelX = side * (visibleOuterRadius + labelOffsetEffective);
        const labelY = rotatedY;
        // elbow point: short horizontal stub that starts at the outer circumference
        const elbowX = rotatedX + side * elbowOffsetEffective;
        const elbowY = rotatedY;
        // final connector point: small vertical line down from the label anchor
        const connectorX = labelX;
        const connectorY = labelY + connectorLen;
        // return polyline points: outer centroid -> elbow -> horizontal to labelX -> vertical down
        return [[rotatedX, rotatedY], [elbowX, elbowY], [labelX, labelY], [connectorX, connectorY]];
      })
      .style('fill', 'none')
      .style('stroke', 'black')
      .style('stroke-width', '1px')
      .style('opacity', function(d) {
        // Hide polyline for zero values
        const key = d.data[0];
        const entry = (self.chartData || []).find((s: any) => s.label === key);
        return (entry && entry.value === 0) ? 0 : 0.9;
      });

    // Draw external labels on left or right and align text anchor (in non-rotated container)
    // compute numeric total using precise sum of chartData values for accurate percentage calculation
    const amountTotalNum = (this.chartData && this.chartData.length) ? 
      this.chartData.map(s => s.value || 0).reduce((a, b) => a + b, 0) : 0;

    // if any slice is <0.05%, mark near-100% slices as approximate
    const hasSmallSlice = amountTotalNum > 0 && (this.chartData || []).some((s: any) => {
      const p = (s.value / amountTotalNum) * 100;
      return p > 0 && p < 0.05;
    });
    container
      .selectAll('labelSlices')
      .data(data_ready)
      .enter()
      .append('text')
      .attr('x', function(d) {
        // compute rotated outer centroid X to determine side consistently with the polyline
        const outer = outerArc.centroid(d) || arc.centroid(d);
        let ox = outer[0];
        let oy = outer[1];
        if (!outer || isNaN(ox) || isNaN(oy)) {
          const midAngle = (d.startAngle + d.endAngle) / 2;
          ox = Math.cos(midAngle - Math.PI / 2) * visibleOuterRadius;
          oy = Math.sin(midAngle - Math.PI / 2) * visibleOuterRadius;
        }
        const rotatedX = ox * Math.cos(rotationRad) - oy * Math.sin(rotationRad);
        const side = (rotatedX < 0) ? -1 : 1;
        // Keep text inside chart bounds by anchoring inward from connector point.
        const connectorX = side * (visibleOuterRadius + labelOffsetEffective);
        return connectorX + (side < 0 ? 12 : -12);
      })
      .attr('y', function(d) {
        // compute rotated outer centroid Y and then push text below the connector
        const outer = outerArc.centroid(d) || arc.centroid(d);
        let ox = outer[0];
        let oy = outer[1];
        if (!outer || isNaN(ox) || isNaN(oy)) {
          const midAngle = (d.startAngle + d.endAngle) / 2;
          ox = Math.cos(midAngle - Math.PI / 2) * visibleOuterRadius;
          oy = Math.sin(midAngle - Math.PI / 2) * visibleOuterRadius;
        }
        const rotatedX = ox * Math.cos(rotationRad) - oy * Math.sin(rotationRad);
        const rotatedY = ox * Math.sin(rotationRad) + oy * Math.cos(rotationRad);
        return rotatedY + connectorLen + Math.max(12, Math.floor(labelFontPx * 0.8)); // push text below connector with dynamic gap
      })
      .style('text-anchor', function(d) {
        const midAngle = (d.startAngle + d.endAngle) / 2;
        let ox = Math.cos(midAngle - Math.PI / 2) * visibleOuterRadius;
        let oy = Math.sin(midAngle - Math.PI / 2) * visibleOuterRadius;
        const rotatedX = ox * Math.cos(rotationRad) - oy * Math.sin(rotationRad);
        const side = (rotatedX < 0) ? -1 : 1;
        // Left labels grow rightwards, right labels grow leftwards.
        return side < 0 ? 'start' : 'end';
      })
      .style('font-size', `${labelFontPx}px`)
      .style('fill', '#000000')
      .each(function(d) {
        // per-label rendering: value on top, percentage below
        const el = d3.select(this);
        const key = d.data[0];
        const entry = (self.chartData || []).find((s: any) => s.label === key);
        if (!entry) return;
        // Hide labels for zero values completely
        if (entry.value === 0) {
          el.style('opacity', 0);
          return;
        }
        if ((d.endAngle - d.startAngle) <= minAngleToShow && entry.value !== 0) {
          return;
        }
        // formatted value (use original value from chartData, not the display value)
        const valueStr = self.digitFormatter.transform(entry.value, 1);
        // compute percentage of the numeric total
        const pct = amountTotalNum && amountTotalNum !== 0 ? (entry.value / amountTotalNum) * 100 : 0;
        const pctStr = pct > 0 && pct < 0.05
          ? '<0.05%'
          : (hasSmallSlice && pct.toFixed(1) === '100.0' && pct < 100
            ? '\u223C' + pct.toFixed(1) + '%'
            : pct.toFixed(1) + '%');
        // append tspans: first line value, second line percentage
        el.append('tspan')
          .attr('x', el.attr('x'))
          .attr('dy', '0')
          .text(valueStr)
          .style('font-size', `${labelFontPx}px`)
          .style('font-weight', '600');
        el.append('tspan')
          .attr('x', el.attr('x'))
          .attr('dy', '1.15em')
          .attr('class', 'percent')
          .text('('+pctStr+')')
          .style('font-size', `${labelFontPx}px`);
      })
      .on('mouseover', () => {})
      .on('mousemove', function() {})
      .on('mouseout', () => {});
      

      // if(this.showMiddleTitle) {
      //    this.label1 = container.append("text")
      //   .attr("text-anchor", "middle")
      //   // .text(this.titleMiddle)
      //   .style("font-size", 13)
      //   .style("font-weight", 500);  
      // }
    
    // console.log(label1.attr("transform"));
    // console.log(d3.transform(label1.attr("transform")).translate[0])
    container.append("text")
      .attr("text-anchor", "middle")
      .text(this.amountTotal)
      .style("font-size", 20)
      .style("font-weight", 500)
      // .attr('y', this.showMiddleTitle ? 15 : 5)
      // .selectAll("tspan")
      // .data(d => {
      //   console.log(d)
      //   // const lines = `${title(d.data)}`.split(/\n/);
      //   // return (d.endAngle - d.startAngle) > 0.25 ? lines : lines.slice(0, 1);
      // })

    this.chartReady.next(true);
  }
}

